import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Select,
  Card,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Modal,
  Form,
  InputNumber,
  Upload,
  message,
  Popconfirm,
  Badge,
  Tooltip,
  Divider,
  Tabs,
  Alert,
} from "antd";
import {
  CompassOutlined,
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import activityCatalogService from "@/services/activityCatalogService";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORIES = [
  "Sightseeing",
  "Adventure",
  "Water Sports",
  "Cultural",
  "Entry Ticket",
  "Transfer",
  "Cruise",
  "Day Tour",
  "Entertainment",
  "Other",
];

const SAMPLE_CSV = `Title,Destination,Category,Duration,Price,Inclusions,Description,Location
Water Sports Combo (Jet Ski & Banana Boat),Goa,Water Sports,2 Hours,1800,Life Jacket;Instructor;Safety Gear,Exciting water adventure at Calangute,Calangute Beach
Burj Khalifa 124th Floor Ticket,Dubai,Entry Ticket,2 Hours,3500,General Admission,At the Top Observatory Deck access,Downtown Dubai
Full Day South Bali Cultural Tour,Bali,Sightseeing,8 Hours,2500,Private Transport;Guide;Lunch,Visit Uluwatu temple and Kecak dance,Kuta Bali
Grand Canyon Helicopter Tour,Las Vegas,Adventure,4 Hours,15000,Pickup;Helicopter Flight;Champagne,Aerial tour over the Canyon,Las Vegas Strip`;

export default function ActivityMasterManager() {
  const [activities, setActivities] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDestination, setFilterDestination] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");

  // Multi-select for bulk delete
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Single Add/Edit Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const imageUrlValue = Form.useWatch("imageUrl", form);

  // Custom image upload handler for single activity modal
  const handleImageUploadCustom = async ({ file, onSuccess, onError }) => {
    try {
      setImageUploading(true);
      const formData = new FormData();
      formData.append("image", file);

      const res = await activityCatalogService.uploadImage(formData);
      if (res?.data?.imageUrl) {
        form.setFieldsValue({ imageUrl: res.data.imageUrl });
        message.success("Activity image uploaded successfully!");
        if (onSuccess) onSuccess("Ok");
      } else {
        throw new Error("No image URL returned");
      }
    } catch (err) {
      console.error("Image upload error:", err);
      message.error("Failed to upload activity image");
      if (onError) onError(err);
    } finally {
      setImageUploading(false);
    }
  };

  // Bulk Upload Modal
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkDestination, setBulkDestination] = useState("");
  const [rawCsvText, setRawCsvText] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);

  // Fetch Destinations
  const fetchDestinations = async () => {
    try {
      const res = await activityCatalogService.getDestinations();
      if (res?.data) {
        setDestinations(res.data);
      }
    } catch (err) {
      console.error("Failed to load destinations:", err);
    }
  };

  // Fetch Activities
  const fetchActivities = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        destination: filterDestination !== "all" ? filterDestination : undefined,
        category: filterCategory !== "all" ? filterCategory : undefined,
        page,
        limit: pagination.pageSize,
      };

      const res = await activityCatalogService.getActivities(params);
      if (res?.data) {
        setActivities(res.data);
        setPagination({
          ...pagination,
          current: page,
          total: res.meta?.total || res.data.length,
        });
      }
    } catch (err) {
      console.error("Failed to load activities:", err);
      message.error("Failed to load activity master catalog");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDestinations();
    fetchActivities(1);
  }, [filterDestination, filterCategory]);

  const handleSearch = () => {
    fetchActivities(1);
  };

  // Delete Single Activity
  const handleDeleteSingle = async (id) => {
    try {
      await activityCatalogService.deleteActivity(id);
      message.success("Activity deleted");
      fetchActivities(pagination.current);
      fetchDestinations();
    } catch (err) {
      console.error(err);
      message.error("Failed to delete activity");
    }
  };

  // Bulk Delete Selected
  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      const res = await activityCatalogService.bulkDeleteActivities(selectedRowKeys);
      message.success(res?.message || "Selected activities deleted successfully");
      setSelectedRowKeys([]);
      fetchActivities(1);
      fetchDestinations();
    } catch (err) {
      console.error(err);
      message.error("Failed to delete selected activities");
    }
  };

  // Open Single Create Modal
  const handleOpenCreate = () => {
    setEditingActivity(null);
    form.resetFields();
    form.setFieldsValue({
      category: "Sightseeing",
      currency: "INR",
      price: 0,
      duration: "2-3 Hours",
    });
    setModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (record) => {
    setEditingActivity(record);
    form.setFieldsValue({
      ...record,
      inclusions: Array.isArray(record.inclusions) ? record.inclusions.join(", ") : record.inclusions || "",
    });
    setModalOpen(true);
  };

  // Save Single Activity Form
  const handleSaveActivity = async (values) => {
    try {
      setSubmitting(true);
      const payload = {
        ...values,
        inclusions: values.inclusions
          ? values.inclusions.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };

      if (editingActivity) {
        await activityCatalogService.updateActivity(editingActivity._id, payload);
        message.success("Activity updated successfully!");
      } else {
        await activityCatalogService.createActivity(payload);
        message.success("Activity added to Master Catalog!");
      }

      setModalOpen(false);
      form.resetFields();
      fetchActivities(pagination.current);
      fetchDestinations();
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Failed to save activity");
    } finally {
      setSubmitting(false);
    }
  };

  // Parse CSV text into array of objects
  const parseCsvToObjects = (text, defaultDest) => {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];

    const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
    const records = [];

    for (let i = 1; i < lines.length; i++) {
      // CSV line splitting handling commas inside quotes
      const row = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || lines[i].split(",");
      const cleanRow = row.map((val) => val.trim().replace(/^"|"$/g, ""));

      const record = {};
      headers.forEach((head, idx) => {
        record[head] = cleanRow[idx] || "";
      });

      if (defaultDest && (!record.Destination || !record.destination)) {
        record.destination = defaultDest;
      }

      records.push(record);
    }
    return records;
  };

  // Process Bulk Upload
  const handleExecuteBulkUpload = async () => {
    if (!rawCsvText.trim()) {
      message.warning("Please paste or upload CSV data first.");
      return;
    }

    try {
      setBulkUploading(true);
      const parsedRecords = parseCsvToObjects(rawCsvText, bulkDestination);

      if (parsedRecords.length === 0) {
        message.error("Could not parse valid records from provided CSV data.");
        setBulkUploading(false);
        return;
      }

      const res = await activityCatalogService.bulkUploadActivities(
        parsedRecords,
        bulkDestination
      );

      if (res?.success) {
        message.success(`🎉 Uploaded ${res.count} activities successfully!`);
        setBulkModalOpen(false);
        setRawCsvText("");
        setBulkDestination("");
        fetchActivities(1);
        fetchDestinations();
      }
    } catch (err) {
      console.error("Bulk upload error:", err);
      message.error(err?.response?.data?.message || "Failed to bulk upload activities");
    } finally {
      setBulkUploading(false);
    }
  };

  // File upload handler (reads file as text)
  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setRawCsvText(content);
      message.success(`Loaded "${file.name}" into bulk parser`);
    };
    reader.readAsText(file);
    return false; // Prevent auto POST upload
  };

  // Download Sample CSV
  const handleDownloadSampleCsv = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "Activity_Master_Sample_Upload.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Table Columns
  const columns = [
    {
      title: "Photo",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: 90,
      render: (imgUrl, record) => (
        <div style={{ position: "relative", width: 64, height: 48, borderRadius: 8, overflow: "hidden", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e2e8f0" }}>
          {imgUrl ? (
            <img
              src={imgUrl}
              alt={record.title}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <CompassOutlined style={{ fontSize: 20, color: "#94a3b8" }} />
          )}
        </div>
      ),
    },
    {
      title: "Title & Details",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{text}</div>
          {record.description && (
            <Paragraph
              ellipsis={{ rows: 1 }}
              style={{ margin: 0, fontSize: 12, color: "#64748b" }}
            >
              {record.description}
            </Paragraph>
          )}
          {record.inclusions && record.inclusions.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
              {record.inclusions.map((inc, idx) => (
                <Tag key={idx} color="green" style={{ fontSize: 10, borderRadius: 4 }}>
                  ✓ {inc}
                </Tag>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Destination",
      dataIndex: "destination",
      key: "destination",
      render: (dest) => (
        <Tag color="geekblue" style={{ borderRadius: 6, fontWeight: 600, fontSize: 12 }}>
          📍 {dest}
        </Tag>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (cat) => (
        <Tag color="purple" style={{ borderRadius: 6 }}>
          {cat || "Sightseeing"}
        </Tag>
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (dur) => (
        <Text style={{ fontSize: 13, color: "#475569" }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {dur || "2-3 Hours"}
        </Text>
      ),
    },
    {
      title: "Price (Rate / pax)",
      dataIndex: "price",
      key: "price",
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
      render: (val) => (
        <span style={{ fontWeight: 800, color: "#059669", fontSize: 15 }}>
          ₹{(val || 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Activity">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "#1677ff" }} />}
              onClick={() => handleOpenEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this activity?"
            description="Are you sure you want to remove this activity from catalog?"
            onConfirm={() => handleDeleteSingle(record._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
          >
            <Tooltip title="Delete Activity">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px", maxWidth: 1400, margin: "0 auto" }}>
      {/* PAGE HEADER */}
      <Card
        style={{
          marginBottom: 20,
          borderRadius: 16,
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          color: "#ffffff",
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
        }}
        bodyStyle={{ padding: "24px" }}
      >
        <Row align="middle" justify="space-between">
          <Col xs={24} md={14}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: 14,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CompassOutlined style={{ fontSize: 26, color: "#38bdf8" }} />
              </div>
              <div>
                <Title level={3} style={{ color: "#ffffff", margin: 0, fontWeight: 800 }}>
                  Activity Master Catalog & Bulk Manager
                </Title>
                <Text style={{ color: "#94a3b8", fontSize: 14 }}>
                  Manage destination activities, sightseeings, transfers, and bulk upload per destination
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} md={10} style={{ textAlign: "right", marginTop: 12 }}>
            <Space wrap>
              <Button
                icon={<UploadOutlined />}
                style={{
                  borderRadius: 10,
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#ffffff",
                  borderColor: "rgba(255,255,255,0.3)",
                }}
                onClick={() => setBulkModalOpen(true)}
              >
                Bulk Upload Activities
              </Button>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{
                  borderRadius: 10,
                  backgroundColor: "#38bdf8",
                  borderColor: "#38bdf8",
                  color: "#0f172a",
                  fontWeight: 700,
                }}
                onClick={handleOpenCreate}
              >
                + Add Activity
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* FILTER & SEARCH BAR */}
      <Card
        style={{ borderRadius: 14, marginBottom: 20, borderColor: "#e2e8f0" }}
        bodyStyle={{ padding: "16px 20px" }}
      >
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} sm={10} md={8}>
            <Input
              placeholder="Search activity title, keyword, location..."
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>

          <Col xs={12} sm={7} md={5}>
            <Select
              style={{ width: "100%" }}
              value={filterDestination}
              onChange={(val) => setFilterDestination(val)}
              placeholder="Filter Destination"
            >
              <Option value="all">🌍 All Destinations</Option>
              {destinations.map((d) => (
                <Option key={d} value={d}>
                  📍 {d}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={12} sm={7} md={5}>
            <Select
              style={{ width: "100%" }}
              value={filterCategory}
              onChange={(val) => setFilterCategory(val)}
              placeholder="Filter Category"
            >
              <Option value="all">📁 All Categories</Option>
              {CATEGORIES.map((cat) => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={6} style={{ textAlign: "right" }}>
            <Space wrap align="center">
              {selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={`Delete ${selectedRowKeys.length} selected activities?`}
                  description="This action cannot be undone."
                  onConfirm={handleBulkDelete}
                  okText="Yes, Delete All"
                  cancelText="Cancel"
                >
                  <Button danger type="primary" icon={<DeleteOutlined />}>
                    Delete Selected ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              )}

              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchActivities(1);
                  fetchDestinations();
                }}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* MAIN DATA TABLE */}
      <Card style={{ borderRadius: 14, borderColor: "#e2e8f0" }} bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={activities}
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page) => fetchActivities(page),
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} activities cataloged`,
          }}
        />
      </Card>

      {/* SINGLE ADD / EDIT MODAL */}
      <Modal
        title={editingActivity ? "Edit Activity Master" : "➕ Add Activity to Master Catalog"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        okText={editingActivity ? "Update Activity" : "Create Activity"}
        destroyOnClose
        width={650}
      >
        <Form form={form} layout="vertical" onFinish={handleSaveActivity} style={{ marginTop: 12 }}>
          <Row gutter={12}>
            <Col span={16}>
              <Form.Item
                name="title"
                label="Activity Title"
                rules={[{ required: true, message: "Activity title is required" }]}
              >
                <Input placeholder="e.g. Scuba Diving & Snorkeling Combo" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="destination"
                label="Destination"
                rules={[{ required: true, message: "Destination is required" }]}
              >
                <Input placeholder="e.g. Goa, Dubai, Bali" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="category" label="Category" initialValue="Sightseeing">
                <Select>
                  {CATEGORIES.map((c) => (
                    <Option key={c} value={c}>
                      {c}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="duration" label="Duration" initialValue="2-3 Hours">
                <Input placeholder="e.g. Full Day, 4 Hours" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="price"
                label="Price / Rate per pax (₹)"
                rules={[{ required: true, message: "Price is required" }]}
              >
                <InputNumber style={{ width: "100%" }} min={0} placeholder="1500" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="location" label="Location / Meeting Point">
                <Input placeholder="e.g. Baga Beach Jetty, North Goa" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierName" label="Supplier / Vendor (Optional)">
                <Input placeholder="e.g. Goan Water Adventures Pvt Ltd" />
              </Form.Item>
            </Col>
          </Row>

          {/* IMAGE UPLOAD & PREVIEW / DELETE SECTION */}
          <Form.Item label="Activity Image (Optional)">
            <Row gutter={12} align="middle">
              <Col span={16}>
                <Form.Item name="imageUrl" noStyle>
                  <Input placeholder="Image URL (e.g. https://... or uploaded image path)" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Upload
                  customRequest={handleImageUploadCustom}
                  showUploadList={false}
                  accept="image/*"
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={imageUploading}
                    style={{ width: "100%" }}
                  >
                    Upload Photo
                  </Button>
                </Upload>
              </Col>
            </Row>

            {imageUrlValue && (
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12, background: "#f8fafc", padding: 8, borderRadius: 8, border: "1px dashed #cbd5e1" }}>
                <img
                  src={imageUrlValue}
                  alt="Activity Preview"
                  style={{ width: 80, height: 50, objectFit: "cover", borderRadius: 6 }}
                />
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 12, display: "block" }}>Uploaded Photo Preview</Text>
                  <Text type="secondary" ellipsis style={{ fontSize: 11, maxWidth: 300, display: "block" }}>
                    {imageUrlValue}
                  </Text>
                </div>
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    form.setFieldsValue({ imageUrl: "" });
                    message.info("Activity image removed");
                  }}
                >
                  Delete Image
                </Button>
              </div>
            )}
          </Form.Item>

          <Form.Item name="inclusions" label="Inclusions (Comma separated)">
            <Input placeholder="e.g. Hotel pickup, Safety gear, Life jacket, Refreshments" />
          </Form.Item>

          <Form.Item name="description" label="Detailed Description / Notes">
            <TextArea rows={3} placeholder="Provide details, timing, safety requirements..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* BULK UPLOAD MODAL */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <FileExcelOutlined style={{ fontSize: 22, color: "#52c41a" }} />
            <div>
              <Title level={5} style={{ margin: 0 }}>
                Bulk Upload Activities for Any Destination
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Import activities via CSV file or paste spreadsheet columns
              </Text>
            </div>
          </div>
        }
        open={bulkModalOpen}
        onCancel={() => setBulkModalOpen(false)}
        onOk={handleExecuteBulkUpload}
        confirmLoading={bulkUploading}
        okText="Upload & Import Activities"
        width={720}
        destroyOnClose
      >
        <div style={{ marginTop: 12 }}>
          <Alert
            message="Flexible Bulk Import System"
            description="Upload CSV file OR paste formatted CSV/Excel data directly below. Destination can be defined per row in CSV column 'Destination' or set via default override below."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={12} align="middle" style={{ marginBottom: 14 }}>
            <Col span={14}>
              <Text strong>Set Default Destination Override (Optional):</Text>
              <Input
                placeholder="e.g. Dubai, Goa, Kerala (used if row lacks destination)"
                value={bulkDestination}
                onChange={(e) => setBulkDestination(e.target.value)}
                style={{ marginTop: 4 }}
              />
            </Col>

            <Col span={10} style={{ textAlign: "right" }}>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadSampleCsv}
                style={{ borderRadius: 8, marginTop: 22 }}
              >
                Download Sample CSV
              </Button>
            </Col>
          </Row>

          <div style={{ marginBottom: 16 }}>
            <Upload.Dragger
              beforeUpload={handleFileUpload}
              accept=".csv,.txt"
              showUploadList={false}
              style={{ padding: "16px 0", background: "#f8fafc", borderRadius: 12 }}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined style={{ color: "#1677ff", fontSize: 32 }} />
              </p>
              <p className="ant-upload-text" style={{ fontWeight: 600 }}>
                Click or drag CSV file to this area
              </p>
              <p className="ant-upload-hint">Supports CSV files with Title, Destination, Category, Duration, Price columns</p>
            </Upload.Dragger>
          </div>

          <Divider style={{ margin: "14px 0" }}>OR PASTE CSV DATA DIRECTLY</Divider>

          <TextArea
            rows={8}
            placeholder={`Title,Destination,Category,Duration,Price,Inclusions,Description\nJet Ski Ride,Goa,Water Sports,30 Mins,800,Life jacket;Instructor,Thrilling ride\nBurj Khalifa 124th Floor,Dubai,Entry Ticket,2 Hours,3500,Entry pass,Observatory view`}
            value={rawCsvText}
            onChange={(e) => setRawCsvText(e.target.value)}
            style={{ fontFamily: "monospace", fontSize: 12 }}
          />
        </div>
      </Modal>
    </div>
  );
}
